import LegalPageLayout from '../../components/feature/LegalPageLayout';

const sections = [
  { id: 'acceptance', title: 'Acceptance of Terms' },
  { id: 'description', title: 'Description of Service' },
  { id: 'acceptable-use', title: 'Acceptable Use' },
  { id: 'user-responsibilities', title: 'User Responsibilities' },
  { id: 'tool-submissions', title: 'Tool Submissions' },
  { id: 'intellectual-property', title: 'Intellectual Property' },
  { id: 'third-party-links', title: 'Third-Party Links' },
  { id: 'disclaimers', title: 'Disclaimers' },
  { id: 'limitation-liability', title: 'Limitation of Liability' },
  { id: 'indemnification', title: 'Indemnification' },
  { id: 'termination', title: 'Termination' },
  { id: 'changes-to-terms', title: 'Changes to Terms' },
  { id: 'governing-law', title: 'Governing Law' },
  { id: 'contact', title: 'Contact Information' },
];

export default function TermsOfServicePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://toolnova.ai';
  return (
    <LegalPageLayout
      title="Terms of Service"
      lastUpdated="January 2025"
      sections={sections}
      seo={{
        title: 'Terms of Service - ToolNova Usage Agreement | ToolNova',
        description: 'Read the ToolNova Terms of Service to understand your rights and responsibilities when using our AI tools directory and services.',
        keywords: 'toolnova terms of service, usage agreement, terms and conditions, AI directory terms',
        canonical: `${siteUrl}/terms-of-service`,
        schema: {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Terms of Service',
          description: 'ToolNova Terms of Service — your rights and responsibilities when using our AI tools directory.',
          url: `${siteUrl}/terms-of-service`,
          publisher: {
            '@type': 'Organization',
            name: 'ToolNova',
            url: siteUrl,
          },
        },
      }}
    >
      <section id="acceptance">
        <h2>Acceptance of Terms</h2>
        <p>
          Welcome to Toolnova. By accessing or using our website and services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any part of these terms, you may not use our services.
        </p>
        <p>
          These Terms of Service apply to all visitors, users, and others who access or use the Toolnova website and directory services.
        </p>
      </section>

      <section id="description">
        <h2>Description of Service</h2>
        <p>
          Toolnova is an AI tools discovery platform that provides:
        </p>
        <ul>
          <li>A curated directory of AI tools and software</li>
          <li>Tool reviews, descriptions, and categorization</li>
          <li>Search and filtering functionality to discover AI tools</li>
          <li>Blog content with insights and guides about AI tools</li>
          <li>A submission system for tool creators to list their products</li>
        </ul>
        <p>
          Toolnova serves as an informational resource and directory. We do not develop, sell, or directly provide the AI tools listed in our directory. We are not responsible for the functionality, availability, or quality of third-party tools.
        </p>
      </section>

      <section id="acceptable-use">
        <h2>Acceptable Use</h2>
        <p>
          You agree to use Toolnova only for lawful purposes and in accordance with these Terms. You agree not to:
        </p>
        <ul>
          <li>Use the service in any way that violates any applicable federal, state, local, or international law or regulation</li>
          <li>Engage in any conduct that restricts or inhibits anyone's use or enjoyment of the service</li>
          <li>Attempt to gain unauthorized access to any portion of the website or any systems or networks connected to the service</li>
          <li>Use any automated system, including robots, spiders, or scrapers, to access the service without our express written permission</li>
          <li>Introduce any viruses, trojan horses, worms, logic bombs, or other malicious or technologically harmful material</li>
          <li>Submit false, misleading, or fraudulent information when submitting tools or contacting us</li>
          <li>Impersonate or attempt to impersonate Toolnova, a Toolnova employee, another user, or any other person or entity</li>
          <li>Use the service to transmit spam, chain letters, or other unsolicited communications</li>
        </ul>
      </section>

      <section id="user-responsibilities">
        <h2>User Responsibilities</h2>
        <p>
          As a user of Toolnova, you are responsible for:
        </p>
        
        <h3>Information Accuracy</h3>
        <p>
          Ensuring that any information you provide to us, including tool submissions and contact information, is accurate, current, and complete.
        </p>

        <h3>Due Diligence</h3>
        <p>
          Conducting your own research and due diligence before using any AI tools listed in our directory. Toolnova provides information for discovery purposes, but you are responsible for evaluating whether a tool meets your specific needs.
        </p>

        <h3>Third-Party Interactions</h3>
        <p>
          Understanding that when you click on links to third-party AI tools, you are subject to their terms of service and privacy policies. Toolnova is not responsible for your interactions with third-party services.
        </p>

        <h3>Security</h3>
        <p>
          Maintaining the security of your device and internet connection when accessing Toolnova and any linked third-party services.
        </p>
      </section>

      <section id="tool-submissions">
        <h2>Tool Submissions</h2>
        <p>
          If you submit an AI tool to our directory, you represent and warrant that:
        </p>
        <ul>
          <li>You have the authority to submit the tool on behalf of the tool creator or company</li>
          <li>All information provided is accurate, truthful, and not misleading</li>
          <li>The tool does not violate any laws, regulations, or third-party rights</li>
          <li>You have the right to use any logos, images, or content submitted with the tool listing</li>
        </ul>
        
        <h3>Review and Approval</h3>
        <p>
          Toolnova reserves the right to review, approve, reject, or remove any tool submission at our sole discretion. We do not guarantee that submitted tools will be listed in our directory.
        </p>

        <h3>Modifications</h3>
        <p>
          We may edit tool descriptions, categories, or other information for clarity, consistency, or quality purposes. We will make reasonable efforts to maintain the accuracy of the information.
        </p>
      </section>

      <section id="intellectual-property">
        <h2>Intellectual Property</h2>
        
        <h3>Toolnova Content</h3>
        <p>
          The Toolnova website, including its design, layout, text, graphics, logos, and functionality, is owned by Toolnova and protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative works from our content without express written permission.
        </p>

        <h3>User-Submitted Content</h3>
        <p>
          When you submit content to Toolnova (such as tool information, logos, or descriptions), you grant us a non-exclusive, worldwide, royalty-free license to use, display, reproduce, and distribute that content in connection with operating and promoting our directory services.
        </p>

        <h3>Third-Party Trademarks</h3>
        <p>
          Tool names, logos, and trademarks displayed in our directory are the property of their respective owners. The inclusion of any tool in our directory does not imply endorsement by or affiliation with the tool creator, unless explicitly stated.
        </p>
      </section>

      <section id="third-party-links">
        <h2>Third-Party Links</h2>
        <p>
          Toolnova contains links to third-party websites and services, including the AI tools listed in our directory. These links are provided for your convenience and information.
        </p>
        <p>
          We do not control, endorse, or assume responsibility for the content, privacy policies, or practices of any third-party websites or services. You acknowledge and agree that Toolnova shall not be responsible or liable, directly or indirectly, for any damage or loss caused by or in connection with the use of any third-party content, goods, or services.
        </p>
        <p>
          We strongly advise you to read the terms of service and privacy policies of any third-party websites or services that you visit.
        </p>
      </section>

      <section id="disclaimers">
        <h2>Disclaimers</h2>
        <p>
          <strong>Toolnova is provided on an "as is" and "as available" basis without warranties of any kind, either express or implied.</strong>
        </p>
        
        <h3>No Warranty</h3>
        <p>
          We do not warrant that:
        </p>
        <ul>
          <li>The service will be uninterrupted, timely, secure, or error-free</li>
          <li>The information provided in our directory is accurate, complete, or current</li>
          <li>Any tools listed in our directory will meet your requirements or expectations</li>
          <li>Any errors in the service will be corrected</li>
        </ul>

        <h3>Information Accuracy</h3>
        <p>
          While we strive to provide accurate and up-to-date information about AI tools, we make no representations or warranties about the accuracy, reliability, or completeness of any tool descriptions, reviews, or other content on our website.
        </p>

        <h3>Third-Party Tools</h3>
        <p>
          Toolnova does not endorse, guarantee, or assume responsibility for any AI tools listed in our directory. We are not liable for the performance, functionality, availability, pricing, or any other aspect of third-party tools.
        </p>
      </section>

      <section id="limitation-liability">
        <h2>Limitation of Liability</h2>
        <p>
          To the fullest extent permitted by applicable law, Toolnova and its affiliates, officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:
        </p>
        <ul>
          <li>Loss of profits, revenue, or data</li>
          <li>Loss of business opportunities</li>
          <li>Damage to reputation</li>
          <li>Cost of substitute services</li>
          <li>Any damages arising from your use of or inability to use the service</li>
          <li>Any damages arising from your use of third-party AI tools discovered through our directory</li>
        </ul>
        <p>
          This limitation applies whether the alleged liability is based on contract, tort, negligence, strict liability, or any other basis, even if Toolnova has been advised of the possibility of such damage.
        </p>
        <p>
          In jurisdictions that do not allow the exclusion or limitation of liability for consequential or incidental damages, our liability shall be limited to the maximum extent permitted by law.
        </p>
      </section>

      <section id="indemnification">
        <h2>Indemnification</h2>
        <p>
          You agree to defend, indemnify, and hold harmless Toolnova and its affiliates, officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including reasonable attorneys' fees and costs, arising out of or in any way connected with:
        </p>
        <ul>
          <li>Your access to or use of the service</li>
          <li>Your violation of these Terms of Service</li>
          <li>Your violation of any third-party rights, including intellectual property rights</li>
          <li>Any content or information you submit to Toolnova</li>
          <li>Your use of any AI tools discovered through our directory</li>
        </ul>
      </section>

      <section id="termination">
        <h2>Termination</h2>
        <p>
          We reserve the right to terminate or suspend your access to Toolnova immediately, without prior notice or liability, for any reason, including but not limited to:
        </p>
        <ul>
          <li>Breach of these Terms of Service</li>
          <li>Fraudulent, abusive, or illegal activity</li>
          <li>Submission of false or misleading information</li>
          <li>Violation of intellectual property rights</li>
        </ul>
        <p>
          Upon termination, your right to use the service will immediately cease. All provisions of these Terms which by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
        </p>
      </section>

      <section id="changes-to-terms">
        <h2>Changes to Terms</h2>
        <p>
          We reserve the right to modify or replace these Terms of Service at any time at our sole discretion. We will provide notice of any material changes by updating the "Last Updated" date at the top of these Terms and, where appropriate, by posting a notice on our website.
        </p>
        <p>
          Your continued use of Toolnova after any changes to these Terms constitutes acceptance of those changes. We encourage you to review these Terms periodically.
        </p>
      </section>

      <section id="governing-law">
        <h2>Governing Law</h2>
        <p>
          These Terms of Service shall be governed by and construed in accordance with the laws of the jurisdiction in which Toolnova operates, without regard to its conflict of law provisions.
        </p>
        <p>
          Any disputes arising from or relating to these Terms or your use of Toolnova shall be resolved through binding arbitration in accordance with the rules of the applicable arbitration association, except where prohibited by law.
        </p>
      </section>

      <section id="contact">
        <h2>Contact Information</h2>
        <p>
          If you have any questions about these Terms of Service, please contact us:
        </p>
        <ul>
          <li><strong>Email:</strong> toolnova.hq@gmail.com</li>
          <li><strong>Contact Form:</strong> Available on our Contact page</li>
        </ul>
      </section>
    </LegalPageLayout>
  );
}